/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, { useState } from 'react'
import { ZIndexProps } from './model'
import classNames from 'classnames'
import styles from '../style/index.css'
import { Popover } from '@targetprocess/popover'
import { Dropdown } from '@targetprocess/dropdown'
import { Button } from '@targetprocess/button'
import * as dateMath from 'date-arithmetic'
import { CloseIcon } from '@targetprocess/icons'
import intl from '../utils/intl/intl'

export enum DateSortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending'
}

export interface DateHeaderProps extends ZIndexProps {
  sortDirection?: DateSortDirection
  beforeDate?: Date
  afterDate?: Date
  onClearFiltersClick?: () => void
  onDateChange?: (dateFilter: { afterDate?: Date; beforeDate?: Date }) => void
  hasClearButton: boolean
}

export const getDatesRange = () => {
  const date = new Date()
  const result: Date[] = []
  for (let i = 0; i < 14; ++i) {
    result.push(dateMath.subtract(date, 1 * i, 'day'))
  }
  return result
}

export const getTimeRange = () => {
  return Array(24)
    .fill(0)
    .map((_, i) => ({
      id: i
    }))
}

export const renderTime = (value: { id: number } | undefined): string => {
  if (!value || typeof value.id !== 'number') return ''
  const hour = Math.floor(value.id)
  if (hour < 0 || hour > 23 || !Number.isFinite(hour)) return ''
  return `${hour}:00`
}

interface DatePickersProps {
  beforeDate?: Date
  afterDate?: Date
  onSetClick: (dateFilter: { beforeDate?: Date; afterDate?: Date }) => void
  onOpenDropdown?: () => void
  onCloseDropdown?: () => void
}

const isValidDateRange = (beforeDate: Date | undefined, afterDate: Date | undefined) => {
  if (!beforeDate || !afterDate) {
    return true
  }
  return afterDate.getTime() < beforeDate.getTime()
}

const getFullDate = (date: Date | undefined, hours: number | undefined) => {
  if (!date) {
    return undefined
  }
  const newDate = new Date(date)
  newDate.setHours(hours ?? 0, 0, 0, 0)
  return newDate
}

const normalizeDate = (d?: Date | string | number): Date | undefined => {
  if (!d) return undefined

  // More robust validation using Object.prototype.toString to prevent prototype pollution
  if (Object.prototype.toString.call(d) === '[object Date]' && !isNaN((d as Date).getTime())) {
    return d as Date
  }

  // Only allow string or number conversion, not objects
  if (typeof d === 'string' || typeof d === 'number') {
    try {
      const parsed = new Date(d)
      if (!isNaN(parsed.getTime())) return parsed
    } catch {
      return undefined
    }
  }

  return undefined
}

const safeFormatDate = (date: Date | undefined): string => {
  if (!date) return ''

  try {
    // Validate it's a real Date object
    if (Object.prototype.toString.call(date) !== '[object Date]' || isNaN(date.getTime())) {
      return ''
    }

    // Extract validated numeric components
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()

    // Validate components are within expected ranges
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return ''
    }
    if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) {
      return ''
    }

    // Use browser's locale-aware formatting
    return date.toLocaleDateString()
  } catch {
    return ''
  }
}

const safeFormatDateTime = (date: Date | undefined): string => {
  if (!date) return ''

  try {
    // Validate it's a real Date object
    if (Object.prototype.toString.call(date) !== '[object Date]' || isNaN(date.getTime())) {
      return ''
    }

    // Extract validated numeric components
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const seconds = date.getSeconds()

    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      !Number.isFinite(seconds)
    ) {
      return ''
    }
    if (
      year < 1900 ||
      year > 2100 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      return ''
    }

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  } catch {
    return ''
  }
}

export const DatePickers = ({
  onSetClick,
  beforeDate: beforeDateProp,
  afterDate: afterDateProp,
  onCloseDropdown,
  onOpenDropdown
}: DatePickersProps) => {
  const initialBeforeDate = normalizeDate(beforeDateProp)
  const initialAfterDate = normalizeDate(afterDateProp)

  const normalizeTime = (value: any): { id: number } | undefined => {
    if (!value || typeof value.id !== 'number') return undefined
    const hour = Math.floor(value.id)
    if (hour < 0 || hour > 23 || !Number.isFinite(hour)) return undefined
    return { id: hour }
  }

  const [dateRange] = useState(getDatesRange())
  const [timeRange] = useState(getTimeRange())
  const [beforeDate, setBeforeDate] = useState(initialBeforeDate)
  const [afterDate, setAfterDate] = useState(initialAfterDate)
  const [beforeTime, setBeforeTime] = useState(
    initialBeforeDate
      ? { id: initialBeforeDate.getHours() }
      : undefined
  )
  const [afterTime, setAfterTime] = useState(
    initialAfterDate
      ? { id: initialAfterDate.getHours() }
      : undefined
  )

  const newBeforeDate = getFullDate(beforeDate, beforeTime?.id)
  const newAfterDate = getFullDate(afterDate, afterTime?.id)
  const isValidRange = isValidDateRange(newBeforeDate, newAfterDate)


  return (
    <div className={styles['date-range__container']}>
      <div className={styles['date-range__container__column']}>
        {intl.formatMessage('From:')}
        <div className={styles['date-range__container__select-item']}>
          <Dropdown
            kind={isValidRange ? 'default' : 'error'}
            onMenuOpen={onOpenDropdown}
            onMenuClose={onCloseDropdown}
            isSearchable={false}
            options={dateRange}
            closeMenuOnSelect
            value={afterDate}
            onChange={(value: any) => setAfterDate(normalizeDate(value))}
            renderSingleValue={(value) => {
              const normalized = normalizeDate(value as any)
              return safeFormatDate(normalized)
            }}
            renderOption={(value: Date) => {
              const normalized = normalizeDate(value as any)
              return safeFormatDate(normalized)
            }}
            getOptionValue={(option: any) => option.getTime()}
          />
        </div>
        <div className={styles['date-range__container__select-item']}>
          <Dropdown
            kind={isValidRange ? 'default' : 'error'}
            onMenuOpen={onOpenDropdown}
            onMenuClose={onCloseDropdown}
            isSearchable={false}
            options={timeRange}
            value={afterTime}
            closeMenuOnSelect
            onChange={(value: any) => setAfterTime(normalizeTime(value))}
            renderSingleValue={(value: any) => renderTime(normalizeTime(value))}
            renderOption={(value: any) => renderTime(normalizeTime(value))}
          />
        </div>
      </div>
      <div className={styles['date-range__container__column']}>
        {intl.formatMessage('To:')}
        <div className={styles['date-range__container__select-item']}>
          <Dropdown
            kind={isValidRange ? 'default' : 'error'}
            onMenuOpen={onOpenDropdown}
            onMenuClose={onCloseDropdown}
            isSearchable={false}
            options={dateRange}
            value={beforeDate}
            closeMenuOnSelect
            onChange={(value: any) => setBeforeDate(normalizeDate(value))}
            renderSingleValue={(value) => {
              const normalized = normalizeDate(value as any)
              return safeFormatDate(normalized)
            }}
            renderOption={(value: Date) => {
              const normalized = normalizeDate(value as any)
              return safeFormatDate(normalized)
            }}
            getOptionValue={(option: any) => option.getTime()}
          />
        </div>
        <div className={styles['date-range__container__select-item']}>
          <Dropdown
            kind={isValidRange ? 'default' : 'error'}
            onMenuOpen={onOpenDropdown}
            onMenuClose={onCloseDropdown}
            isSearchable={false}
            options={timeRange}
            closeMenuOnSelect
            value={beforeTime}
            onChange={(value: any) => setBeforeTime(normalizeTime(value))}
            renderSingleValue={(value: any) => renderTime(normalizeTime(value))}
            renderOption={(value: any) => renderTime(normalizeTime(value))}
          />
        </div>
      </div>
      <Button
        kind="primary"
        size="L"
        disabled={!isValidRange}
        onClick={() => {
          onSetClick({ beforeDate: newBeforeDate, afterDate: newAfterDate })
        }}
      >
        {intl.formatMessage('Apply')}
      </Button>
    </div>
  )
}

export const DateHeader = ({
  onClearFiltersClick,
  onDateChange,
  beforeDate,
  afterDate,
  hasClearButton,
  popoverZIndex
}: DateHeaderProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [hasOpenDropdown, setHasOpenDropdown] = useState(false)

  // Validate dates immediately at component boundary to prevent any untrusted data flow
  const validatedBeforeDate = normalizeDate(beforeDate)
  const validatedAfterDate = normalizeDate(afterDate)

  const hasDateFilters = validatedBeforeDate || validatedAfterDate

  return (
    <Popover
      zIndex={popoverZIndex}
      getPopoverPosition={'bottom'}
      isOpen={isPopoverOpen}
      onOuterAction={() => !hasOpenDropdown && setIsPopoverOpen(false)}
      body={
        <Popover.Body noPadding>
          <DatePickers
            beforeDate={validatedBeforeDate}
            afterDate={validatedAfterDate}
            onCloseDropdown={() => setHasOpenDropdown(false)}
            onOpenDropdown={() => setHasOpenDropdown(true)}
            onSetClick={(dateFilter) => {
              setIsPopoverOpen(false)
              onDateChange && onDateChange(dateFilter)
            }}
          />
        </Popover.Body>
      }
    >
      <div className={classNames(styles['log__header'], styles['log__header__with-action-item'])}>
        <div
          className={classNames(
            styles['log__header__name'],
            hasDateFilters && styles['log__header__name--active']
          )}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        >
          {intl.formatMessage('Date & Time')}
        </div>
        {hasClearButton && (
          <div
            className={classNames(styles['log__header__action-item'])}
            onClick={onClearFiltersClick}
          >
            <CloseIcon color="#ae304a" size="small" />
            {intl.formatMessage('Clear filter')}
          </div>
        )}
      </div>
    </Popover>
  )
}

export const DateRow = ({ item }: { item: { createdAt?: Date } }) => {
  const date = normalizeDate(item.createdAt)

  return (
    <div className={classNames(styles['log__cell-item'])}>
      <div className={classNames(styles['log__cell-item__value'])}>
        {safeFormatDateTime(date)}
      </div>
    </div>
  )
}